import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { runAiJob } from "@/lib/aiJobs";
import { Button } from "@/components/ui/button";
import { TEST_IDS } from "@/constants/testIds";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, ArrowUpRight, Info, ShoppingBag, Sparkles } from "lucide-react";

export default function Shopping() {
  const [gaps, setGaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openInfo, setOpenInfo] = useState(null); // gap object shown in modal

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/shopping/suggestions");
      setGaps(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-16">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="eyebrow">Wearables · Clothes & accessories</p>
          <h1 className="editorial-h1 mt-3">Buy with intention.</h1>
        </div>
        <Button
          data-testid={TEST_IDS.shopping.refreshBtn}
          variant="outline"
          onClick={load}
          className="rounded-none uppercase tracking-[0.2em] text-xs"
        >Refresh analysis</Button>
      </div>

      {loading && (
        <div className="border border-border p-16 text-center mt-10">
          <Loader2 size={28} className="mx-auto animate-spin" />
          <p className="eyebrow mt-4 pulse-soft">Analyzing your closet</p>
        </div>
      )}

      {/* Top Pick — hero recommendation with AI product image on the side */}
      {!loading && gaps.length > 0 && (() => {
        const top = gaps[0];
        const links = Object.entries(top.affiliate_links || {});
        const primary = links[0];
        const similarUrl = `https://www.google.com/search?tbm=shop&q=${encodeURIComponent(top.item)}`;
        return (
          <div
            className="mt-10 border border-accent bg-accent text-accent-foreground p-6 lg:p-10 grid md:grid-cols-[220px_1fr_auto] gap-6 items-center"
            data-testid="shop-top-pick"
          >
            <RecommendationImage item={top.item} description={top.reasoning} className="hidden md:block h-52 w-52" tone="dark" />
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.25em] opacity-80">Top pick · #1 wardrobe gap</p>
              <h2 className="font-serif text-4xl mt-2 break-words">{top.item}</h2>
              <p className="text-[11px] uppercase tracking-[0.2em] opacity-80 mt-3">Budget · {top.budget_range_inr || "—"}</p>
              <button
                type="button"
                onClick={() => setOpenInfo(top)}
                data-testid="shop-top-info"
                className="mt-4 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] underline underline-offset-4 opacity-90 hover:opacity-100"
              >
                <Info size={12} /> Why we picked this
              </button>
            </div>
            <div className="flex flex-col gap-2 min-w-[200px]">
              {primary && (
                <a
                  href={primary[1]}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="shop-top-buy"
                  className="bg-accent-foreground text-accent uppercase tracking-[0.2em] text-[11px] h-11 inline-flex items-center justify-center px-4 hover:opacity-90"
                >
                  Buy on {primary[0]} <ArrowUpRight size={12} className="ml-2" />
                </a>
              )}
              <a
                href={similarUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="shop-top-similar"
                className="border border-accent-foreground/40 uppercase tracking-[0.2em] text-[11px] h-11 inline-flex items-center justify-center px-4 hover:bg-accent-foreground/10"
              >
                Find similar <ArrowUpRight size={12} className="ml-2" />
              </a>
            </div>
          </div>
        );
      })()}

      {/* Grid of recommendations — pure visual, text hidden behind info button. */}
      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {gaps.map((g, i) => (
          <article
            key={i}
            data-testid={TEST_IDS.shopping.card(i)}
            className="border border-border bg-card flex flex-col group"
          >
            <RecommendationImage item={g.item} description={g.reasoning} className="aspect-square w-full" />
            <div className="p-5 flex-1 flex flex-col">
              <div className="flex items-baseline justify-between gap-3">
                <p className="font-serif text-xl tracking-tight break-words">{g.item}</p>
                <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground shrink-0">#{i + 1}</span>
              </div>

              {/* Explanation is behind the info button — NOT on the card. */}
              <div className="mt-3 flex items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={() => setOpenInfo(g)}
                  data-testid={`shop-info-btn-${i}`}
                  aria-label="Why we recommend this"
                  className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground hover:text-accent transition-colors"
                >
                  <Info size={13} /> Details
                </button>
                <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  Budget · {g.budget_range_inr || "—"}
                </p>
              </div>

              <div className="mt-auto pt-5 grid grid-cols-2 gap-2">
                {Object.entries(g.affiliate_links || {}).slice(0, 2).map(([name, url], idx) => (
                  <a
                    key={name}
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`shop-buy-${i}-${idx}`}
                    className="border border-border px-2.5 py-2 text-[11px] uppercase tracking-[0.18em] flex items-center justify-between hover:border-foreground transition"
                  >
                    {idx === 0 ? `Buy · ${name}` : name}
                    <ArrowUpRight size={12} />
                  </a>
                ))}
                <a
                  href={`https://www.google.com/search?tbm=shop&q=${encodeURIComponent(g.item)}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`shop-similar-${i}`}
                  className="col-span-2 border border-border px-2.5 py-2 text-[11px] uppercase tracking-[0.18em] flex items-center justify-between hover:border-foreground transition text-accent"
                >
                  Find similar
                  <ArrowUpRight size={12} />
                </a>
              </div>
            </div>
          </article>
        ))}
      </div>

      {!loading && gaps.length === 0 && (
        <div className="border border-dashed border-border p-16 mt-10 text-center">
          <p className="font-serif text-3xl">Your wardrobe looks well-rounded.</p>
          <p className="text-sm text-muted-foreground mt-3">Add more items to discover new gaps.</p>
        </div>
      )}

      {/* Info modal — reveals reasoning, scores, budget. */}
      <Dialog open={!!openInfo} onOpenChange={(o) => !o && setOpenInfo(null)}>
        <DialogContent className="max-w-lg rounded-none" data-testid="shop-info-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl tracking-tight break-words">{openInfo?.item}</DialogTitle>
          </DialogHeader>
          {openInfo && (
            <div className="space-y-5">
              <div>
                <p className="eyebrow flex items-center gap-2"><Sparkles size={12} /> Why we recommend this</p>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{openInfo.reasoning || "No detail available."}</p>
              </div>
              <div className="grid grid-cols-3 gap-3 text-[10px] uppercase tracking-[0.18em]">
                <Stat label="Need" value={openInfo.need_score} />
                <Stat label="Versatility" value={openInfo.versatility_score} />
                <Stat label="Trend" value={openInfo.trend_score} />
              </div>
              <p className="text-xs">
                <span className="eyebrow mr-2">Budget</span>
                {openInfo.budget_range_inr || "Flexible"}
              </p>
              {Object.keys(openInfo.affiliate_links || {}).length > 0 && (
                <div>
                  <p className="eyebrow mb-2 flex items-center gap-2"><ShoppingBag size={12} /> Shop this</p>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(openInfo.affiliate_links).map(([name, url]) => (
                      <a
                        key={name}
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className="border border-border px-2.5 py-2 text-[11px] uppercase tracking-[0.18em] flex items-center justify-between hover:border-foreground transition"
                      >
                        {name} <ArrowUpRight size={12} />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <p className="text-muted-foreground">{label}</p>
      <p className="font-serif text-2xl mt-1">{value || 0}</p>
    </div>
  );
}

// AI-generated product visual (Nano Banana). Lazily fetches from
// /shopping/recommendation-image the first time this card mounts. The
// backend caches by SHA-1 of the item text so repeat views are instant.
// Uncached items now come back via async ai_jobs → runAiJob polls
// GET /api/ai-jobs/{job_id} until the image is ready.
function RecommendationImage({ item, description, className = "", tone = "light" }) {
  const [state, setState] = useState({ url: null, loading: true, failed: false });

  useEffect(() => {
    const ac = new AbortController();
    setState({ url: null, loading: true, failed: false });
    runAiJob(
      () => api.post("/shopping/recommendation-image", {
        item,
        description: description?.slice(0, 240) || "",
      }),
      { signal: ac.signal },
    )
      .then((data) => {
        if (ac.signal.aborted) return;
        setState({ url: fileUrl(data.image_path), loading: false, failed: false });
      })
      .catch(() => {
        if (ac.signal.aborted) return;
        setState({ url: null, loading: false, failed: true });
      });
    return () => { ac.abort(); };
  }, [item, description]);

  const isDark = tone === "dark";
  const skeleton = isDark ? "bg-accent-foreground/10" : "bg-muted/40";

  if (state.loading) {
    return (
      <div className={`${className} ${skeleton} flex items-center justify-center overflow-hidden`} data-testid="shop-rec-image-loading">
        <Loader2 size={22} className={`animate-spin ${isDark ? "text-accent-foreground/70" : "text-muted-foreground"}`} />
      </div>
    );
  }

  if (state.failed || !state.url) {
    return (
      <div className={`${className} ${skeleton} flex items-center justify-center`} data-testid="shop-rec-image-failed">
        <ShoppingBag size={22} className={isDark ? "text-accent-foreground/50" : "text-muted-foreground"} />
      </div>
    );
  }

  return (
    <img
      src={state.url}
      alt={item}
      loading="lazy"
      data-testid="shop-rec-image"
      className={`${className} object-cover`}
    />
  );
}
